import { useCallback, useEffect, useState } from "react";
import api, { fmtErr } from "../lib/api";
import { toast } from "sonner";

/**
 * useLiveRoomFeed — owns the /vibes/feed query, poll interval, and tab-visibility
 * refresh. Loading state is exposed so the caller can show a scanning overlay.
 *
 * Preserves original LiveRoom behavior:
 *   - 20-second poll interval
 *   - visibilitychange listener re-fetches when tab regains focus
 *   - setFeedLoaded(true) is guaranteed to fire on both success AND error paths
 *   - Error path toasts fmtErr(e) — user-facing, matches original
 *
 * `enabled` gates the effect so we don't poll before presence is confirmed.
 */
export function useLiveRoomFeed({ enabled }) {
  const [feed, setFeed] = useState({ vibes: [], live_count: 0, scope: "none", venue: null });
  const [feedLoaded, setFeedLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/vibes/feed");
      setFeed(data);
      setFeedLoaded(true);
    } catch (e) {
      toast.error(fmtErr(e));
      setFeedLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVis);
    const poll = setInterval(refresh, 20000);
    return () => {
      clearInterval(poll);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [enabled, refresh]);

  return { feed, setFeed, feedLoaded, setFeedLoaded, refresh };
}
